import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { DocumentExpirationAlert, DocumentExpirationSummary } from '@/types/documentNotification'
import documentNotificationService from '@/services/documentNotificationService'

export const useDocumentNotificationStore = defineStore('documentNotification', () => {
  const alerts = ref<DocumentExpirationAlert[]>([])
  const summary = ref<DocumentExpirationSummary>({
    total_alerts: 0,
    expired_count: 0,
    expiring_soon_count: 0,
  })
  const isLoading = ref(false)
  const lastFetched = ref<Date | null>(null)
  const error = ref<string | null>(null)

  const hasAlerts = computed(() => summary.value.total_alerts > 0)
  const hasExpired = computed(() => summary.value.expired_count > 0)
  const hasExpiringSoon = computed(() => summary.value.expiring_soon_count > 0)

  async function fetchAlerts(force = false) {
    if (!force && lastFetched.value && (Date.now() - lastFetched.value.getTime() < 30000)) {
      return
    }

    isLoading.value = true
    error.value = null

    try {
      const data = await documentNotificationService.getExpiringDocuments()
      alerts.value = data.alerts || []
      summary.value = data.summary || {
        total_alerts: alerts.value.length,
        expired_count: alerts.value.filter(a => a.is_expired).length,
        expiring_soon_count: alerts.value.filter(a => !a.is_expired).length,
      }
      lastFetched.value = new Date()
    } catch (err: any) {
      error.value = err?.message || 'Failed to load notifications'
    } finally {
      isLoading.value = false
    }
  }

  function reset() {
    alerts.value = []
    summary.value = { total_alerts: 0, expired_count: 0, expiring_soon_count: 0 }
    lastFetched.value = null
    error.value = null
  }

  return {
    alerts,
    summary,
    isLoading,
    lastFetched,
    error,
    hasAlerts,
    hasExpired,
    hasExpiringSoon,
    fetchAlerts,
    reset,
  }
})

export default useDocumentNotificationStore

